// src/services/api/openaiApi.ts
import { lf } from "./langfuse";
import { db } from "../../firebase"; // ⬅️ adjust path if needed
import { collection, getDocs } from "firebase/firestore";

const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions";
// ❌ No more process.env here

interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

// Cache the key so we don't hit Firestore on every call
let cachedOpenAIApiKey: string | null = null;

async function getOpenAIApiKey(): Promise<string> {
  if (cachedOpenAIApiKey) return cachedOpenAIApiKey;

  // Collection: open_ai_key, only one doc with field "api_key"
  const snapshot = await getDocs(collection(db, "open_ai_key"));

  if (snapshot.empty) {
    throw new Error(
      "No document found in Firestore collection 'open_ai_key' (expected 1 doc with field 'api_key')."
    );
  }

  const doc = snapshot.docs[0];
  const data = doc.data() as { api_key?: string };

  if (!data.api_key) {
    throw new Error(
      "Field 'api_key' not found on the document in 'open_ai_key' collection."
    );
  }

  cachedOpenAIApiKey = data.api_key;
  return cachedOpenAIApiKey;
}

export async function callOpenAI(
  messages: ChatMessage[],
  model: string = "gpt-3.5-turbo",
  temperature: number = 0.0,
  prompt_name: string = "chat_completion"
): Promise<string> {
  const requestBody = {
    model,
    messages,
    temperature,
  };

  // 1️⃣ Start a top-level trace for this entire call
  const trace = lf.trace({
    name: "openai_" + prompt_name,
  });

  // 2️⃣ Within that trace, start a generation span
  const generation = trace.generation({
    name: prompt_name,
    model,
    modelParameters: { temperature },
    input: messages,
  });

  try {
    // 🔑 Get the OpenAI API key from Firestore
    const OPENAI_API_KEY = await getOpenAIApiKey();

    const response = await fetch(OPENAI_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorDetails = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorDetails}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;

    // 4️⃣ Mark the generation as successful, record the output
    await generation.end({
      output: content,
    });

    await lf.flush();

    return content;
  } catch (error) {
    // 6️⃣ If something went wrong, record the error
    await generation.end({
      output: error instanceof Error ? error.message : String(error),
    });
    await lf.flush();

    console.error("OpenAI API call error:", error);
    throw error;
  }
}